import { zodResolver } from '@hookform/resolvers/zod';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import cn from 'classnames';
import { useCallback, useState, useEffect, useRef } from 'react';
import {
  FormProvider,
  useForm,
  useFormContext,
  useFieldArray,
} from 'react-hook-form';
import toast from 'react-hot-toast/headless';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { IconTrash2 } from 'components/UI/Icons/Trash2';

import {
  getCandidateCommentaryOptions,
  postCandidateIntroTemplatesMutation,
  putIntroductionTemplateMutation,
  postCandidateHighlightsV2Mutation,
  putHighlightMutation,
} from 'client/@tanstack/react-query.gen';
import type { PipelineCandidateSearchFilterSortResponse } from 'client/types.gen';
import { Card } from 'components/Card';
import { CardDetail } from 'components/CardDetail';
import { IconEdit } from 'components/UI/Icons/Edit';
import { IconPlus } from 'components/UI/Icons/Plus';
import { Button } from 'components/UI/Tailwind/Button';
import { Dialog } from 'components/UI/Tailwind/Dialog';
import { FormInput } from 'components/UI/Tailwind/FormInput';
import { FormTextArea } from 'components/UI/Tailwind/FormTextArea';
import { Tooltip } from 'components/UI/Tailwind/Tooltip';
import { getFormattedDate } from 'helpers/date';
import { Schema as ParentSchema } from 'components/NewRecommendations/Step2';

const commentarySchema = z.object({
  highlights: z.array(
    z.object({
      highlight: z.string().min(1),
      last_modified: z.string().optional(),
      uuid: z.string().optional(),
    }),
  ),
  introduction_templates: z.array(
    z.object({
      body: z.string().min(1),
      title: z.string().min(1),
      last_modified: z.string().optional(),
      uuid: z.string().optional(),
    }),
  ),
});

type CommentarySchema = z.infer<typeof commentarySchema>;

interface FormState {
  index: number;
  type: 'highlights' | 'introduction_templates' | undefined;
}

export function CandidateAboutDialog({
  candidate,
  isOpen,
  onClose: _onClose,
}: {
  candidate: PipelineCandidateSearchFilterSortResponse;
  isOpen: boolean;
  onClose: VoidFunction;
}) {
  const { t } = useTranslation();
  const candidateId = candidate.uuid;

  const queryClient = useQueryClient();

  const { data: commentary } = useQuery({
    placeholderData: keepPreviousData,
    ...getCandidateCommentaryOptions({
      path: {
        candidate_uuid: candidateId,
      },
    }),
  });

  const getDefaultValues = useCallback(() => {
    if (!commentary) {
      return {
        highlights: [],
        introduction_templates: [],
      };
    }
    const parsed = commentarySchema.safeParse({
      highlights: commentary.highlights.filter(
        (highlight) => !highlight.hidden,
      ),
      introduction_templates: commentary.introduction_templates.filter(
        (template) => !template.hidden,
      ),
    });
    if (!parsed.success) {
      return {
        highlights: [],
        introduction_templates: [],
      };
    }
    return parsed.data;
  }, [commentary]);

  const methods = useForm<CommentarySchema>({
    defaultValues: getDefaultValues(),
    mode: 'onChange',
    resolver: zodResolver(commentarySchema),
  });

  const templateFieldArray = useFieldArray({
    control: methods.control,
    name: 'introduction_templates' as const,
  });
  const highlightFieldArray = useFieldArray({
    control: methods.control,
    name: 'highlights' as const,
  });

  useEffect(() => {
    methods.reset(getDefaultValues());
  }, [getDefaultValues, methods]);

  const parentMethods = useFormContext<ParentSchema>();

  const [formState, setFormState] = useState<FormState>({
    index: -1,
    type: undefined,
  });

  const onClose = useCallback(() => {
    _onClose();
    setFormState({
      index: -1,
      type: undefined,
    });
    methods.reset();
  }, [_onClose, methods]);

  const postTemplate = useMutation({
    ...postCandidateIntroTemplatesMutation(),
    onSuccess: (result) => {
      parentMethods.setValue(
        `candidates.${candidateId}.about_uuid`,
        result.uuid,
      );
    },
  });

  const putTemplate = useMutation({
    ...putIntroductionTemplateMutation(),
    onSuccess: (result) => {
      parentMethods.setValue(
        `candidates.${candidateId}.about_uuid`,
        result.uuid,
      );
    },
  });

  const postHighlight = useMutation({
    ...postCandidateHighlightsV2Mutation(),
    onSuccess: (result) => {
      parentMethods.setValue(
        `candidates.${candidateId}.highlights_with_selected.${result.uuid}`,
        true,
      );
    },
  });

  const putHighlight = useMutation({
    ...putHighlightMutation(),
    onSuccess: (result, variables) => {
      parentMethods.setValue(
        `candidates.${candidateId}.highlights_with_selected.${variables.path.highlight_uuid}`,
        false,
      );
      parentMethods.setValue(
        `candidates.${candidateId}.highlights_with_selected.${result.uuid}`,
        true,
      );
    },
  });

  return (
    <FormProvider {...methods}>
      <Dialog
        heading={t('modal.editCandidateAbout.title')}
        actionButtonProps={{
          isLoading: methods.formState.isSubmitting,
          variant: 'default-quaternary',
          children: t('modal.editCandidateAbout.close'),
        }}
        cancelButtonProps={{
          variant: 'default-quaternary',
        }}
        open={isOpen}
        onClose={onClose}
        backgroundClassName="bg-white"
        className="max-h-[70vh] overflow-hidden"
        subtitle="These narratives are pulled from the executive’s profile. Updating the content here will reflect changes on the profile as well."
      >
        <div className="max-h-[40vh] overflow-y-auto px-3">
          <span className="flex flex-1 flex-col gap-x-2 px-5">
            <Button
              className="text-14 font-semibold text-gray-800"
              variant="default-link"
              to={`/candidates/$candidateId`}
              params={{ candidateId: candidate.uuid }}
              size={26}
              target="_blank"
            >
              {candidate.name}
            </Button>
            <span className="text-12 font-normal text-gray-600">
              {candidate.current_role}
            </span>
          </span>
          <div className="flex flex-col gap-y-4">
            <Card title={t('page.candidate.about.templates')}>
              <div className="flex flex-col gap-y-4">
                <ul className="flex flex-col gap-y-2">
                  {(templateFieldArray.fields ?? []).map((template, index) =>
                    formState.type === 'introduction_templates' &&
                    formState.index === index ? (
                      <TemplateFormCard
                        isLoading={
                          putTemplate.isPending || postTemplate.isPending
                        }
                        onCancel={() => {
                          methods.resetField(`introduction_templates.${index}`);
                          setFormState({
                            index: -1,
                            type: undefined,
                          });
                        }}
                        onSubmit={() => {
                          const values = methods.getValues(
                            `introduction_templates.${index}`,
                          );
                          if (!template.uuid) {
                            postTemplate.mutate(
                              {
                                body: {
                                  body: values.body,
                                  title: values.title,
                                },
                                path: {
                                  candidate_uuid: candidateId,
                                },
                              },
                              {
                                onSuccess: () => {
                                  queryClient.invalidateQueries({
                                    queryKey: getCandidateCommentaryOptions({
                                      path: { candidate_uuid: candidateId },
                                    }).queryKey,
                                  });
                                  toast.success('Template created');
                                  onClose();
                                },
                                onError: () => {
                                  toast.error('Template creation failed');
                                },
                              },
                            );

                            return;
                          }
                          putTemplate.mutate(
                            {
                              body: {
                                body: values.body,
                                title: values.title,
                              },
                              path: {
                                template_uuid: template.uuid,
                              },
                            },
                            {
                              onSuccess: () => {
                                queryClient.invalidateQueries({
                                  queryKey: getCandidateCommentaryOptions({
                                    path: { candidate_uuid: candidateId },
                                  }).queryKey,
                                });
                                toast.success('Template updated');
                                onClose();
                              },
                              onError: () => {
                                toast.error('Template update failed');
                              },
                            },
                          );
                        }}
                        name="introduction_templates"
                        key={index}
                        index={index}
                        onDelete={() => {
                          templateFieldArray.remove(index);
                          setFormState({
                            index: -1,
                            type: undefined,
                          });
                        }}
                      />
                    ) : (
                      <CardDetail
                        key={index}
                        actions={
                          formState.index === -1 && (
                            <Tooltip
                              place="top"
                              title={t('page.candidate.about.actions.edit')}
                            >
                              {(props) => (
                                <Button
                                  className="!text-gray-600"
                                  iconStart={<IconEdit />}
                                  onClick={() => {
                                    setFormState({
                                      index,
                                      type: 'introduction_templates',
                                    });
                                  }}
                                  size={32}
                                  variant="default-quaternary_DEPRECATED"
                                  {...props}
                                />
                              )}
                            </Tooltip>
                          )
                        }
                        date={t('page.candidate.templates.date', {
                          date: getFormattedDate(
                            template.last_modified ?? new Date().toISOString(),
                          ),
                        })}
                        title={template.title}
                      >
                        {template.body}
                      </CardDetail>
                    ),
                  )}
                </ul>
                <Button
                  className="self-start"
                  iconStart={<IconPlus />}
                  onClick={() => {
                    templateFieldArray.append({
                      body: '',
                      title: '',
                    });
                    setFormState({
                      index: templateFieldArray.fields.length,
                      type: 'introduction_templates',
                    });
                  }}
                  size={32}
                  variant="default-tertiary"
                >
                  {t('page.candidate.templates.addButton')}
                </Button>
              </div>
            </Card>
            <Card title={t('page.candidate.about.highlights')}>
              <div className="flex flex-col gap-y-4">
                <ul className="flex flex-col gap-y-2">
                  {(highlightFieldArray.fields ?? []).map((highlight, index) =>
                    formState.type === 'highlights' &&
                    formState.index === index ? (
                      <HighlightFormCard
                        isLoading={
                          highlight.uuid
                            ? putHighlight.isPending
                            : postHighlight.isPending
                        }
                        key={index}
                        index={index}
                        onCancel={() => {
                          methods.resetField(`highlights.${index}`);
                          setFormState({
                            index: -1,
                            type: undefined,
                          });
                        }}
                        onSubmit={() => {
                          const values = methods.getValues(
                            `highlights.${index}`,
                          );
                          if (!highlight.uuid) {
                            postHighlight.mutate(
                              {
                                path: {
                                  candidate_uuid: candidateId,
                                },
                                body: {
                                  highlight: values.highlight,
                                },
                              },
                              {
                                onSuccess: () => {
                                  queryClient.invalidateQueries({
                                    queryKey: getCandidateCommentaryOptions({
                                      path: { candidate_uuid: candidateId },
                                    }).queryKey,
                                  });
                                  onClose();
                                  toast.success('Highlight created');
                                },
                                onError: () => {
                                  toast.error('Highlight creation failed');
                                },
                              },
                            );
                          } else {
                            putHighlight.mutate(
                              {
                                path: {
                                  highlight_uuid: highlight.uuid,
                                },
                                body: {
                                  highlight: values.highlight,
                                },
                              },
                              {
                                onSuccess: () => {
                                  queryClient.invalidateQueries({
                                    queryKey: getCandidateCommentaryOptions({
                                      path: { candidate_uuid: candidateId },
                                    }).queryKey,
                                  });
                                  onClose();
                                  toast.success('Highlight updated');
                                },
                                onError: () => {
                                  toast.error('Highlight update failed');
                                },
                              },
                            );
                          }
                        }}
                        onDelete={() => {
                          highlightFieldArray.remove(index);
                          setFormState({
                            index: -1,
                            type: undefined,
                          });
                        }}
                      />
                    ) : (
                      <CardDetail
                        key={index}
                        actions={
                          formState.index === -1 && (
                            <Tooltip
                              place="top"
                              title={t('page.candidate.about.actions.edit')}
                            >
                              {(props) => (
                                <Button
                                  className="!text-gray-600"
                                  iconStart={<IconEdit />}
                                  onClick={() => {
                                    setFormState({
                                      index,
                                      type: 'highlights',
                                    });
                                  }}
                                  size={32}
                                  variant="default-quaternary_DEPRECATED"
                                  {...props}
                                />
                              )}
                            </Tooltip>
                          )
                        }
                        date={t('page.candidate.highlights.date', {
                          date: getFormattedDate(
                            highlight.last_modified ?? new Date().toISOString(),
                          ),
                        })}
                        title={t('page.candidate.highlights.title', {
                          count: index + 1,
                        })}
                      >
                        {highlight.highlight}
                      </CardDetail>
                    ),
                  )}
                </ul>
                <Button
                  className="self-start"
                  iconStart={<IconPlus />}
                  onClick={() => {
                    highlightFieldArray.append({
                      highlight: '',
                    });
                    setFormState({
                      index: highlightFieldArray.fields.length,
                      type: 'highlights',
                    });
                  }}
                  size={32}
                  variant="default-tertiary"
                >
                  {t('page.candidate.highlights.addButton')}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </Dialog>
    </FormProvider>
  );
}

function TemplateFormCard({
  name = 'introduction_templates',
  index,
  onDelete,
  onCancel,
  onSubmit,
  isLoading,
}: {
  index: number;
  name?: 'introduction_templates';
  onDelete: VoidFunction;
  onSubmit: VoidFunction;
  onCancel: VoidFunction;
  isLoading: boolean;
}) {
  const { t } = useTranslation();

  const methods = useFormContext<CommentarySchema>();

  const { watch } = methods;
  const [watchUuid] = watch([`${name}.${index}.uuid`]);

  const isPending = isLoading;

  const submitRef = useRef<HTMLButtonElement>(null);

  const onKeyDown: FormTextArea['onKeyDown'] = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (submitRef.current) {
        submitRef.current.click();
      }
    }
  };

  return (
    <div className="flex flex-col gap-y-4 rounded-[8px] bg-gray-50 p-4">
      {!watchUuid && (
        <h3 className="block text-16/120% font-semibold text-gray-800">
          {t('page.candidate.about.newTemplate')}
        </h3>
      )}
      <div className="flex flex-col gap-y-1">
        <h4 className="text-14/120% font-medium text-gray-800">
          {t('page.candidate.about.purpose.title')}
        </h4>
        <p className="text-12/120% text-gray-600">
          {t('page.candidate.about.purpose.hint')}
        </p>
        <FormInput
          className={cn({
            'bg-white': !isPending,
          })}
          disabled={isPending}
          name={`${name}.${index}.title`}
          placeholder={t('page.candidate.about.purpose.placeholder')}
          size={32}
        />
      </div>
      <div className="flex flex-col gap-y-1">
        <h4 className="text-14/120% font-medium text-gray-800">
          {t('page.candidate.about.description.title')}
        </h4>
        <p className="text-12/120% text-gray-600">
          {t('page.candidate.about.description.hint')}
        </p>
        <FormTextArea
          disabled={isPending}
          maxLength={600}
          name={`${name}.${index}.body`}
          placeholder={t('page.candidate.about.description.placeholder')}
          showCount
          onKeyDown={onKeyDown}
        />
      </div>
      <div className="flex gap-x-2">
        {watchUuid ? (
          <Button
            disabled={isPending}
            onClick={onCancel}
            size={32}
            variant="default-quaternary"
          >
            {t('prompts.cancel')}
          </Button>
        ) : (
          <Button
            disabled={isPending}
            onClick={onDelete}
            iconStart={<IconTrash2 />}
            size={32}
            variant="destructive-quaternary"
          >
            {t('prompts.delete')}
          </Button>
        )}
        <Button
          data-ph-capture-attribute-form-submit="candidate.about-dialog-template"
          disabled={!methods.formState.isValid}
          isLoading={isPending}
          size={32}
          onClick={onSubmit}
          variant="default-secondary"
          ref={submitRef}
        >
          {t('prompts.save')}
        </Button>
      </div>
    </div>
  );
}

function HighlightFormCard({
  name = 'highlights',
  index,
  isLoading,
  onSubmit,
  onDelete,
  onCancel,
}: {
  name?: 'highlights';
  index: number;
  isLoading: boolean;
  onSubmit: VoidFunction;
  onDelete: VoidFunction;
  onCancel: VoidFunction;
}) {
  const { t } = useTranslation();

  const methods = useFormContext<CommentarySchema>();

  const { formState } = methods;

  const [watchUuid] = methods.watch([`${name}.${index}.uuid`]);

  const defaultValues = !!watchUuid;

  const isPending = isLoading;

  const submitRef = useRef<HTMLButtonElement>(null);

  const onKeyDown: FormTextArea['onKeyDown'] = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (submitRef.current) {
        submitRef.current.click();
      }
    }
  };

  return (
    <div className="flex flex-col gap-y-2 rounded-[8px] bg-gray-50 p-4">
      <h3
        className={cn('block font-semibold', {
          'text-12/120% text-gray-600': defaultValues,
          'text-14/120% text-gray-800': !defaultValues,
        })}
      >
        {t('page.candidate.highlights.title', {
          count: index + 1,
        })}
      </h3>
      <div className="flex flex-col gap-y-1">
        <h4
          className={cn('font-medium text-gray-600', {
            'text-12/120%': defaultValues,
            'text-14/120%': !defaultValues,
          })}
        >
          {t('page.candidate.about.highlight.title')}
        </h4>
        <FormTextArea
          disabled={isPending}
          maxLength={600}
          name={`${name}.${index}.highlight`}
          placeholder={t('page.candidate.about.highlight.placeholder')}
          showCount
          onKeyDown={onKeyDown}
        />
      </div>
      <div className="flex gap-x-2 pt-2">
        {watchUuid ? (
          <Button
            disabled={isPending}
            onClick={onCancel}
            size={32}
            variant="default-quaternary"
          >
            {t('prompts.cancel')}
          </Button>
        ) : (
          <Button
            disabled={isPending}
            onClick={onDelete}
            iconStart={<IconTrash2 />}
            size={32}
            variant="destructive-quaternary"
          >
            {t('prompts.delete')}
          </Button>
        )}
        <Button
          data-ph-capture-attribute-form-submit="candidate.about-dialog-highlight"
          disabled={!formState.isValid}
          isLoading={isPending}
          size={32}
          onClick={onSubmit}
          variant="default-secondary"
          ref={submitRef}
        >
          {t('prompts.save')}
        </Button>
      </div>
    </div>
  );
}
